import { GetAchievementResponseDto, GetAchievementsData } from '@helios/frontend-api-v2-client';
import { AxiosResponse } from 'axios';
import { getAchievements } from '../api/achievements/achievements';

export const ACHIEVEMENT_EARNED_EVENT = 'achievement-earned';

export const exludedRoutes = ['/api/v2/achievements/:id/view'];

const isUrlExcluded = (url: string, patterns: string[]): boolean => {
  if (!url) return false;

  const urlPath = new URL(url, window.location.origin).pathname;

  return patterns.some((pattern) => {
    const regexPattern = pattern.replace(/:[^/]+/g, '[^/]+');
    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(urlPath);
  });
};

export interface AchievementEarnedEventDetail {
  achievements: GetAchievementResponseDto[];
}

export const dispatchAchievementEarnedEvent = (achievements: GetAchievementResponseDto[]): void => {
  const event = new CustomEvent<AchievementEarnedEventDetail>(ACHIEVEMENT_EARNED_EVENT, {
    detail: { achievements },
  });
  window.dispatchEvent(event);
};

export interface AchievementPollingConfig {
  maxPollingAttempts: number;
  pollingIntervalMs: number;
}

const pollingTimeouts: Set<NodeJS.Timeout> = new Set();
let isPolling = false;

export const clearAchievementPolling = (): void => {
  isPolling = false;
  pollingTimeouts.forEach((timeout) => clearTimeout(timeout));
  pollingTimeouts.clear();
};

export const requestInitialAchievements = async (): Promise<void> => {
  try {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    const params: GetAchievementsData['query'] = {
      earnedSince: oneWeekAgo.toISOString(),
      viewed: false,
      earned: true,
    };

    const response = await getAchievements(params);

    if (response.data && response.data.length > 0) {
      dispatchAchievementEarnedEvent(response.data);
    }
  } catch (error) {
    console.error('Failed to request initial achievements:', error);
  }
};

export const startAchievementPollingAfterRequest = (
  requestTimestamp: Date,
  config: AchievementPollingConfig = {
    maxPollingAttempts: 3,
    pollingIntervalMs: 1000,
  },
): void => {
  clearAchievementPolling();

  isPolling = true;
  let attempts = 0;

  const poll = async () => {
    if (attempts >= config.maxPollingAttempts || !isPolling) {
      isPolling = false;
      return;
    }

    try {
      const params: GetAchievementsData['query'] = {
        earnedSince: requestTimestamp.toISOString(),
        viewed: false,
        earned: true,
      };

      const response = await getAchievements(params);

      if (response.data && response.data.length > 0) {
        isPolling = false;
        dispatchAchievementEarnedEvent(response.data);
        return;
      }

      attempts++;

      if (attempts < config.maxPollingAttempts && isPolling) {
        const timeout = setTimeout(poll, config.pollingIntervalMs);
        pollingTimeouts.add(timeout);
      } else {
        isPolling = false;
      }
    } catch (error) {
      console.error('Failed to poll achievements:', error);
      attempts++;

      if (attempts < config.maxPollingAttempts && isPolling) {
        const timeout = setTimeout(poll, config.pollingIntervalMs);
        pollingTimeouts.add(timeout);
      } else {
        isPolling = false;
      }
    }
  };

  poll();
};

export const getIsAchievementPolling = (): boolean => {
  return isPolling;
};

export const handleAchievementPolling = (response: AxiosResponse): AxiosResponse => {
  const method = response.config.method?.toUpperCase();
  const url = response.request?.responseURL ?? response.config.url;
  const authHeader =
    typeof response.config.headers.Authorization === 'string' ? response.config.headers.Authorization : '';
  const isAuthenticatedRequest = authHeader?.startsWith('Bearer ') && authHeader !== 'Bearer undefined';

  if (
    (method === 'PUT' || method === 'POST' || method === 'PATCH') &&
    response.status >= 200 &&
    response.status < 300 &&
    !isUrlExcluded(url || '', exludedRoutes) &&
    isAuthenticatedRequest
  ) {
    // Subtract 5 seconds to capture achievements awarded during the in-app session
    const now = new Date();
    const requestTimestamp = new Date(now.getTime() - 5000);
    startAchievementPollingAfterRequest(requestTimestamp);
  }

  return response;
};
